import { Directive, ElementRef, Renderer2, HostBinding, ChangeDetectorRef } from '@angular/core';
import { Util } from '@libs/utilities/util';

import { NgEventListenerHelper } from '../event-listener/event-listener-helper';
import { NgEventListener } from '../event-listener/event-listener.data';


@Directive({
    selector: '[ngCustomizableSliderElement]',
    standalone: false
})
export class NgSliderElementDirective {

	//** Helper Variables */
	private eventListenerHelper	: NgEventListenerHelper;
	private eventListeners		: NgEventListener[] = [];

	private positionCount		: number  = 0;
	private dimensionCount		: number  = 0;
	private isAlwaysHide		: boolean = false;
	private isVertical			: boolean = false;
	private scaleValue			: number  = 1;
	private rotateCount			: number  = 0;

	get position()				: number  { return this.positionCount; }
	get dimension()				: number  { return this.dimensionCount; }
	get alwaysHide()			: boolean { return this.isAlwaysHide; }
	get vertical()				: boolean { return this.isVertical; }
	get scale()					: number  { return this.scaleValue; }
	get rotate()				: number  { return this.rotateCount; }

	//** Host Bindings */
	@HostBinding('style.left') 		 left		: string = '';
	@HostBinding('style.width') 	 width		: string = '';
	@HostBinding('style.height') 	 height		: string = '';
	@HostBinding('style.bottom') 	 bottom		: string = '';
	@HostBinding('style.opacity') 	 opacity	: number = 1;
	@HostBinding('style.transform')  transform	: string = '';
	@HostBinding('style.visibility') visibility	: string = 'visible';


	constructor(
		protected elemRef: ElementRef,
		protected renderer: Renderer2,
		protected changeDetectionRef: ChangeDetectorRef
	) {
		this.eventListenerHelper = new NgEventListenerHelper(this.renderer);
	}


	/**------------------------------------------------------
	 * Show / Hide Slider
	 */
	setAlwaysHide(hide: boolean): void {
		this.isAlwaysHide 	= hide;
		this.visibility 	= hide ? 'hidden' : 'visible';
	}

	hide(): void {
		this.opacity = 0;
	}

	show(): void {
		if (this.alwaysHide) return;
		this.opacity = 1;
	}

	isVisible(): boolean {
		if (this.alwaysHide) return false;
		return this.opacity !== 0;
	}


	/**------------------------------------------------------
	 * Update slide behavior
	 */
	setVertical(vertical: boolean): void {

		//0 - set vertical status
		this.isVertical = vertical;

		//1 - check if is vertical
		if (this.isVertical) {
			this.left = '';
			this.width = '';
			return;
		}

		//2 - set bottom and height
		this.bottom = '';
		this.height = '';
	}

	setScale(scale: number): void {
		this.scaleValue = scale;
	}

	setRotate(rotate: number): void {
		this.rotateCount = rotate;
		this.transform = `rotate(${rotate}deg)`;
	}

	getRotate(): number {
		return this.rotateCount;
	}

	setPosition(pos: number): void {

		//0 - update detect changes
		if (this.positionCount !== pos && !this.isRefDestroyed()) this.changeDetectionRef.markForCheck();

		//1 - set count and update position
		this.positionCount = pos;
		if (this.isVertical) this.bottom = `${Math.round(pos)}px`;
			else this.left = `${Math.round(pos)}px`;
	}

	calculateDimension(): void {
		const rectBoundaries: DOMRect = this.getBoundingClientRect();
		if (this.vertical) this.dimensionCount = (rectBoundaries.bottom - rectBoundaries.top) * this.scale;
			else this.dimensionCount = (rectBoundaries.right - rectBoundaries.left) * this.scale;
	}

	setDimension(dim: number): void {

		//0 - update detect changes
		if (this.dimensionCount !== dim && !this.isRefDestroyed()) this.changeDetectionRef.markForCheck();

		//1 - set count and update position
		this.dimensionCount = dim;
		if (this.isVertical) this.height = `${Math.round(dim)}px`;
			else this.width = `${Math.round(dim)}px`;
	}

	getBoundingClientRect(): DOMRect {
		return this.elemRef.nativeElement.getBoundingClientRect();
	}

	/**------------------------------------------------------
	 * Event Listeners
	 */
	on(eventName: string, callback: (event: any) => void, debounceInterval?: number): void {
		const listener: NgEventListener = this.eventListenerHelper.attachEventListener(this.elemRef.nativeElement, eventName, callback, debounceInterval);
		this.eventListeners.push(listener);
	}

	onPassive(eventName: string, callback: (event: any) => void, debounceInterval?: number): void {
		const listener: NgEventListener = this.eventListenerHelper.attachPassiveEventListener(this.elemRef.nativeElement, eventName, callback, debounceInterval);
		this.eventListeners.push(listener);
	}

	off(eventName?: string): void {

		//0 - initialize listeners for keep and remove the events
		let listenersToKeep	 : NgEventListener[];
		let listenersToRemove: NgEventListener[];

		//1 - update listener
		if (!Util.Basic.isUndefined(eventName)) {
			listenersToKeep   = this.eventListeners.filter((event: NgEventListener) => event.eventName !== eventName);
			listenersToRemove = this.eventListeners.filter((event: NgEventListener) => event.eventName === eventName);
		} else {
			listenersToKeep   = [];
			listenersToRemove = this.eventListeners;
		}

		//2 - detach listeners and update keep one
		for (const listener of listenersToRemove) this.eventListenerHelper.detachEventListener(listener);
		this.eventListeners = listenersToKeep;
	}

	private isRefDestroyed(): boolean {
		return Util.Basic.isUndefined(this.changeDetectionRef) || (this.changeDetectionRef as any).destroyed;
	}
}
